import TextComponent from 'Component/Render/Text';
import Renderable from 'Component/Renderable';
import Armature from 'Prototype/Armature';
import TNTModifier from 'Prototype/Level/Card/Modifier/TNT';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { DragonbonesDefault, DragonbonesDefaultMeta, DragonbonesFadeIn } from 'System/Animation/Clip';
import { PlaySound, StopSound } from 'System/Audio/AudioHelper';
import TNTModifierLevelFailTask from 'System/Card/Task/TNTModifierLevelFailTask';
import CardSystem from '../../CardSystem';
import BaseCardModifierHandler from './BaseCardModifierHandler';
import { createSelector, OutputSelector } from 'reselect';
import CardStateHelper from 'State/Card/CardStateHelper';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import { IGameState } from 'State/Game/IGameState';
import CardModifier from 'Prototype/Level/Card/Modifier/CardModifier';
import Game from 'Game';

const timerAudioSelector: OutputSelector<
    [(state: IGameState) => void],
    void,
    (hasActiveTNT: boolean, timer: number) => void
> = createSelector(
    (state: IGameState) => CardStateHelper.hasActiveModifierType(state, CardModifierType.tnt),
    (state: IGameState) => CardStateHelper.getLowestActiveTNTModifierTimer(state),
    (hasActiveTNT, timer) => {
        if (!hasActiveTNT) {
            StopSound('tnt_8_moves_Left');
            return;
        }

        if (timer <= 8 && timer > 0) {
            // Looping sounds cannot be played more than once.
            PlaySound('tnt_8_moves_Left', { loop: true, allowMultipleInstances: false });
        } else {
            StopSound('tnt_8_moves_Left');
        }

        PlaySound('tnt_counter_decrease');
    }
);

export default class TNTModifierHandler extends BaseCardModifierHandler {
    constructor(modifier: CardModifier) {
        super(modifier);

        // Ensure the modifier text is updated when the modifier is created. For some reason `handleModifierInPlay` is
        // not called for modifiers when a level is started and the TNT modifier may show the default text "99". This
        // update circumvents this issue.
        this.updateTimerText(Game.store.getState());
    }

    public handleStateChange(state: IGameState, cardSystem: CardSystem) {
        super.handleStateChange(state, cardSystem);

        timerAudioSelector(state);
    }

    public handleModifierInPlay(state: IGameState, cardSystem: CardSystem) {
        this.updateTimerText(state);

        const modifierStateData = this.getModifierStateData(state);
        const currentTimer = modifierStateData.properties.timer;

        if (!(this.modifier instanceof TNTModifier)) return;

        const layout = this.modifier.getLayout();

        // Only animate the timer change if the value actually changes.
        if (this.modifierComponent.properties.timer !== currentTimer) {
            const bombArmature = layout.tnt_modifier_armature as Armature;
            const ringArmature = layout.tnt_modifier_ring_armature as Armature;
            this.animateTimerChange(bombArmature, ringArmature, currentTimer);
        }

        if (currentTimer === 0 && !cardSystem.taskQueue.some(task => task instanceof TNTModifierLevelFailTask)) {
            cardSystem.taskQueue.queue(new TNTModifierLevelFailTask(this.modifier));
        }
    }

    private updateTimerText(state: IGameState) {
        const modifierStateData = this.getModifierStateData(state);
        const currentTimer = modifierStateData.properties.timer;

        if (!(this.modifier instanceof TNTModifier)) return;

        const layout = this.modifier.getLayout();
        const turnCount = layout.turn_count;

        // Always update timer text to ensure initial value matches component value.
        turnCount.getComponent(TextComponent).text = currentTimer.toString();
    }

    private animateTimerChange(bombArmature: Armature, ringArmature: Armature, currentTimer: number): void {
        const tntAnimationData = this.getTNTMetaData(currentTimer);
        StartAnimationClip(new DragonbonesDefault(bombArmature, tntAnimationData));

        const ringRenderable = ringArmature.getComponent(Renderable);
        // If there is a ring animation to play
        if (this.shouldPlayRingAnimationName(currentTimer)) {
            const ringAnimationName: string = this.getRingAnimationName(currentTimer);
            // If armature is already visible don't fade in
            const Clip = ringRenderable.alpha < 1 ? DragonbonesFadeIn : DragonbonesDefault;
            StartAnimationClip(
                new Clip(ringArmature, {
                    animationName: ringAnimationName,
                    playTimes: 1,
                    concat: false,
                    fadeInTime: 15,
                })
            );
        } else {
            // If no animation, then hide armature
            ringRenderable.alpha = 0;
        }
    }

    private getTNTMetaData(currentTimer: number): DragonbonesDefaultMeta {
        if (currentTimer === 1) {
            return {
                animationName: 'tnt_count_down_1',
            };
        } else if (currentTimer <= 3) {
            return {
                animationName: 'tnt_count_down_3_2',
            };
        } else if (currentTimer <= 8) {
            return {
                animationName: 'tnt_count_down_8_4',
            };
        }

        return {
            animationName: 'tnt_count_down_regular',
            playTimes: 1,
        };
    }

    private shouldPlayRingAnimationName(currentTimer: number): boolean {
        return currentTimer <= 8;
    }

    private getRingAnimationName(currentTimer: number) {
        if (currentTimer <= 3) {
            return 'tnt_count_down_3_2_ring';
        } else if (currentTimer <= 8) {
            return 'tnt_count_down_8_4_ring';
        }
    }
}
